import React, { createContext, useContext, useState, useCallback, FC, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { isEqual, isFunction } from 'lodash';
import {useDispatch} from "react-redux";
import * as actions from "../actions/priceCatalogActions";

type QueryParams = Record<string, string>;

type QueryParamsUpdater = QueryParams | ((prev: QueryParams) => QueryParams);

interface PriceCatalogContextValue {
  queryParams: QueryParams;
  setQueryParamsBase: React.Dispatch<React.SetStateAction<QueryParams>>;
  setQueryParams: (nextQueryParams: QueryParamsUpdater) => void;
  updateTable: () => Promise<void>;
}

interface PriceCatalogProviderProps {
  children: ReactNode;
}

const PriceCatalogContext = createContext<PriceCatalogContextValue | null>(null);

export function usePriceCatalogContext(): PriceCatalogContextValue {
  const context = useContext(PriceCatalogContext);
  if (!context) {
    throw new Error('usePriceCatalogContext must be used within a PriceCatalogProvider');
  }
  return context;
}

export const PriceCatalogProvider: FC<PriceCatalogProviderProps> = ({ children }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [queryParams, setQueryParamsBase] = useState<QueryParams>({});

  const setQueryParams = useCallback((nextQueryParams: QueryParamsUpdater) => {
    setQueryParamsBase((prevQueryParams) => {
      if (isFunction(nextQueryParams)) {
        nextQueryParams = nextQueryParams(prevQueryParams);
      }

      if (isEqual(prevQueryParams, nextQueryParams)) {
        return prevQueryParams;
      }

      const cleaned = Object.fromEntries(
        Object.entries(nextQueryParams).filter(([, v]) => Boolean(v))
      ) as QueryParams;

      const path = history.location.pathname;
      const newQueryParams = new URLSearchParams(cleaned);
      history.replace(`${path}?${newQueryParams.toString()}`);

      return cleaned;
    });
  }, [history]);

  const updateTable = async (): Promise<void> => {
    await dispatch(actions.getPriceCatalogItems(queryParams));
  };

  const value: PriceCatalogContextValue = {
    queryParams,
    setQueryParamsBase,
    setQueryParams,
    updateTable
  };

  return (
    <PriceCatalogContext.Provider value={value}>
      {children}
    </PriceCatalogContext.Provider>
  );
};